import React, { useMemo } from 'react';
import { Header } from '../../common/header.component';
import { DeliveryOrderService } from '../../../services/delivery-order.service';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DeliveryOrderStatus } from '../../common/delivery-order-status.component';
import { DeliveryOrderAssemblyModal } from './delivery-order-assembly-modal';
import { ModalsService } from '../../../services/modals.service';
import { AlertService } from '../../../services/alert.service';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { api, fetcher } from '../../../services/api';
import { DeliveryOrderAssemblyInfo } from './delivery-order-assembly-info';
import { DeliveryOrderDeliveryInfo } from './delivery-order-delivery-info';
import { ClientInfo } from '../../common/client-info';
import { DeliveryOrderFinancesInfo } from './delivery-order-finances-info';
import { DeliveryOrderStatusTable } from './delivery-order-status-table';
import { TrackNumberFormModal } from './trackNumber-form-modal.component';
import classNames from 'classnames';
import btnStyle from './trackNumber-button.module.css';
import { OrderTransactionsInfo } from '../purchase-order/admin-order-transactions-info';
import { Rbac, useRbac } from '../../common/Rbac';
import {
  CleanConversationType,
  CleanDeliveryOrder,
  RbacObject,
} from '../../../export-types/cleaned-types';
import { DeliveryOrderPackageGroupInfo } from './delivery-order-package-group-info';
import { useProfile } from '../../../api/profile';
import { FormattedMessage } from 'react-intl';
import { SupplierInfo } from '../../common/SupplierInfo';
import { ConversationWindow } from '../../common/conversation-window';
import { DeleteDeliveryModal } from './delete-delivery-modal.component';
import { Nav } from 'rsuite';
import NotesInfo from '../../common/notes-info';
import { ConfirmModal } from '../../common/confirm-modal';
import { DeliveryOrderCdekInfo } from './delivery-order-cdek-info';
import { WarningModal } from '../../common/warning-modal';
import { DeliveryOrderPackageModal } from './delivery-order-package-modal';
import { RevertReceivedInRussiaConfirmationModal } from './delivery-order-screen/revert-received-in-russia-confirmation-modal';
import { RecipientInfoButton } from './delivery-order-screen/recipient-info-button';
import { SentToRecipientModal } from './delivery-order-screen/sent-to-recipient-modal';
import { RetailDeliveryDescriptionButton } from './retail-delivery-description-button';
import { RetailDeliveryBulkButton } from './retail-delivery-bulk-button';
import { RetailDescriptionIfo } from '../../common/retail-description-info';
import { trpc, RouterInput } from '../../../api/trpc';
import { useGlobalMutate } from '../../../api/useGlobalMutate';
import { RetailDeliveryOrderStatusTable } from './retail-delivery-order-status-table';

export const DeliveryOrderScreen: React.FC = () => {
  const navigate = useNavigate();
  const { mutate: globalMutate } = useGlobalMutate();
  const { id } = useParams();
  const { data: user } = useProfile();
  const { hasPermission } = useRbac();
  const { data: order, mutate: mutateOrder } = useSWR<CleanDeliveryOrder>(
    `/delivery-order/${id}`,
    fetcher,
  );
  const { trigger: uploadHWB, isMutating: uploadHWBIsMutating } =
    useSWRMutation(
      ['spsr.uploadHWB'],
      (_k, { arg }: { arg: RouterInput['spsr']['uploadHWB'] }) =>
        trpc.spsr.uploadHWB.mutate(arg),
    );

  const {
    trigger: moveOrderToAssembly,
    isMutating: moveOrderToAssemblyIsMutating,
  } = useSWRMutation(
    ['spsr.moveOrderToAssembly'],
    (_k, { arg }: { arg: RouterInput['spsr']['moveOrderToAssembly'] }) =>
      trpc.spsr.moveOrderToAssembly.mutate(arg),
  );

  const shouldFetchEvents =
    !!id &&
    (order?.status === 'retail' || order?.transportation?.type === 'combined');

  const { data: events } = useSWR(
    shouldFetchEvents ? (['delivery-events', id] as const) : null,
    () =>
      trpc.retailDelivery.requestAdmin.events.query({
        deliveryId: id!,
      }),
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = useMemo<CleanConversationType>(() => {
    const chat = searchParams.get('chat') || 'supplier';
    if (!['supplier', 'client'].includes(chat)) {
      return 'supplier';
    }
    return chat as CleanConversationType;
  }, [searchParams]);

  function getTrackNumberForm() {
    ModalsService.createModal(TrackNumberFormModal, {
      deliveryOrder: order!,
    }).then(() => mutateOrder());
  }

  function blockOrder() {
    if (!order) return null;

    if (order.status !== 'sentToRecipient') return null;

    const result = window.confirm(
      `Вы действительно хотите ${
        order.blocked ? 'разблокировать' : 'заблокировать'
      } доставку ?`,
    );
    if (!result) {
      return;
    }

    return DeliveryOrderService.updateDeliveryOrder(
      { blocked: !order.blocked },
      order.id,
    )
      .then(() => {
        AlertService.success();
      })
      .then(() => mutateOrder());
  }

  function deleteOrder() {
    if (
      !order ||
      (order.status !== 'assembly' && order.status !== 'processing')
    )
      return null;
    return ModalsService.createModal(DeleteDeliveryModal, {
      deliveryOrder: {
        id: order.id,
        internalId: order.internalId.toString(),
      },
    })
      .then(() => {
        navigate('/delivery-orders/orders');
      })
      .then(() => {
        AlertService.success('Доставка успешно удалена');
      });
  }

  function nextStep() {
    if (!order) return null;

    switch (order.status) {
      case 'processing':
        return ModalsService.createModal(ConfirmModal, {
          title: 'Разморозка доставки',
          buttonText: `Разморозить доставку`,
        })
          .then(() => {
            return api.post<CleanDeliveryOrder>(
              `/delivery-order/${order.id}/send-to-assembly`,
            );
          })
          .then((response) => {
            AlertService.success();

            void mutateOrder(response.data);
          });

      case 'assembly':
        return ModalsService.createModal(DeliveryOrderPackageModal, {
          deliveryOrderId: order.id,
        }).then(() => mutateOrder());
      case 'packaged':
        return ModalsService.createModal(DeliveryOrderAssemblyModal, {
          deliveryOrderId: order.id,
          composite: !!order.compositeDelivery,
          defaultValues: order,
        }).then(() => mutateOrder());
      case 'sentToRussia': {
        const result = window.confirm(
          'Вы действительно хотите отметить заказ полученным в РФ?',
        );
        if (!result) {
          return;
        }

        return api
          .post<CleanDeliveryOrder>(`/delivery-order/${order.id}/receive`)
          .then((response) => {
            AlertService.success();
            void mutateOrder(response.data);

            return response.data;
          })
          .then((updatedData) => {
            if (updatedData?.cdekOrder && !updatedData?.cdekOrder?.cdekUuid) {
              return ModalsService.createModal(WarningModal, {
                title: 'Доставка в CDEK не была создана',
                buttonText: 'Продолжить',
              });
            }
          });
      }
      case 'receivedInRetailRussia':
      case 'receivedInRussia': {
        ModalsService.createModal(SentToRecipientModal, {
          orderId: order.id,
          readOnly: false,
        });
        break;
      }
      case 'sentToRecipient': {
        const result = window.confirm(
          'Вы действительно хотите отметить заказ полученным клиентом?',
        );
        if (!result) {
          return;
        }

        return DeliveryOrderService.updateDeliveryOrder(
          { status: 'delivered' },
          order.id,
        )
          .then(() => {
            AlertService.success();
          })
          .then(() => mutateOrder())
          .catch((error) => {
            if (!error.response) throw error;

            if (
              error.response.status !== 409 ||
              !error.response.data ||
              !error.response.data.code
            ) {
              throw error;
            }

            if (error.response.data.code === 'blocked') {
              AlertService.error(
                'Доставка на блокировке выдачи клиенту! Снимите блокировку',
              );
            }
            console.log('error', error);
          });
      }
      case 'retail': {
        if (uploadHWBIsMutating || moveOrderToAssemblyIsMutating) return;

        if (!order.retailOrder?.hwbRefNumber) {
          return ModalsService.createModal(ConfirmModal, {
            title: 'Вы действительно хотите отправить накладную?',
            buttonText: 'Отправить накладную',
          })
            .then(() => {
              return uploadHWB({ deliveryOrderId: order.id });
            })
            .then((uploadResult) => {
              if (
                uploadResult?.success &&
                uploadResult?.result?.HWB?.[0]?.Status === 'OK'
              ) {
                globalMutate();
                AlertService.success('Накладная успешно отправлена!');
              } else {
                AlertService.error('Ошибка при отправке накладной');
              }
            })
            .catch((err) => {
              if (err === undefined) return;
              AlertService.error('Ошибка');
            });
        }

        return ModalsService.createModal(ConfirmModal, {
          title: 'Вы действительно хотите отправить заказ на сборку?',
          buttonText: 'Отправить на сборку',
        })
          .then(() => {
            return moveOrderToAssembly({ deliveryOrderId: order.id });
          })
          .then(() => {
            globalMutate();
            AlertService.success('Заказ отправлен на сборку!');
          })
          .catch((err) => {
            if (err === undefined) return;
            AlertService.error('Ошибка');
          });
      }
    }
  }

  const nextStepText = useMemo(() => {
    if (!order) return null;
    if (
      order.compositeDelivery &&
      ['assembled', 'sentToRussia'].includes(order.status)
    ) {
      return null;
    }
    if (
      order.retailOrder &&
      [
        'receivedInRussia',
        'receivedInRetailRussia',
        'sentToRecipient',
      ].includes(order.status)
    ) {
      return null;
    }

    if (user?.role === 'employee' && order.status === 'sentToRecipient')
      return null;

    switch (order.status) {
      case 'processing':
        if (!hasPermission(RbacObject.DeliveryOrder, 'send-to-assembly')) {
          break;
        }

        return 'Разморозить';
      case 'assembly':
        if (!hasPermission(RbacObject.DeliveryOrder, 'write:markSentToRussia'))
          break;

        if (order.compositeDelivery) {
          return (
            <FormattedMessage
              defaultMessage="Укомплектовать"
              id="delivery.btn-label.assembly"
              description="Надпись на кнопке"
            />
          );
        }

        if (order.transportationType === 'standalone') {
          return (
            <FormattedMessage
              defaultMessage="Подтвердить упаковку"
              id="delivery.btn-label.confirmShipmentPackage"
              description="Надпись на кнопке/заголовок"
            />
          );
        }

        break;
      case 'packaged':
        return (
          <FormattedMessage
            defaultMessage="Подтвердить отправку в РФ"
            id="delivery.btn-label.confirmShipmentToTheRussianFederation"
            description="Надпись на кнопке/заголовок"
          />
        );
      case 'sentToRussia':
        if (!hasPermission(RbacObject.Transportation, 'write:receive')) break;

        if (order.transportationType === 'standalone')
          return 'Подтвердить получение в РФ';

        break;
      case 'receivedInRussia':
      case 'receivedInRetailRussia':
        if (!hasPermission(RbacObject.DeliveryOrder, 'write:markSentToRussia'))
          break;

        return 'Подтвердить отправку получателю';
      case 'sentToRecipient':
        if (!hasPermission(RbacObject.DeliveryOrder, 'write:markDelivered'))
          break;

        return 'Клиент получил';
      case 'retail':
        if (!hasPermission(RbacObject.DeliveryOrder, 'write')) break;
        if (!order.retailOrder?.description) {
          break;
        }

        return !order.retailOrder?.hwbRefNumber ? (
          <FormattedMessage
            defaultMessage="Отправить накладную"
            id="delivery.btn-label.sentHwbRefNumber"
            description="Надпись на кнопке"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Отправить на сборку"
            id="delivery.btn-label.sendForAssembly"
            description="Надпись на кнопке"
          />
        );
    }

    return null;
  }, [order, user, hasPermission]);

  if (!order || !user) {
    return null;
  }

  const isOrange =
    order.status === 'retail' && !order.retailOrder?.hwbRefNumber;
  const btnClass = `btn d-flex align-items-center ${isOrange ? 'btn-warning' : 'btn-success'}`;

  return (
    <div className="d-flex flex-column h-100">
      <Header>
        <div className="d-flex flex-row align-items-center">
          <h1 className="me-3 mb-0 d-flex flex-column">
            <FormattedMessage
              defaultMessage="Заказ на доставку {value}"
              id="delivery.label.shippingOrder"
              description="Заголовок на странице"
              values={{ value: 'D' + order.internalId }}
            />
          </h1>
          <DeliveryOrderStatus status={order.status} />
        </div>
        {user?.role === 'admin' ||
        ((user?.accesses.includes('purchase') ||
          user?.accesses.includes('warehouse')) &&
          !!user.supplier) ? (
          <h3 className="me-3 mb-0 d-flex flex-column color-green">
            {order.deliveryMethod?.destination?.city}
          </h3>
        ) : null}
      </Header>
      <div className="d-flex flex-column flex-lg-row flex-grow-1 gap-4">
        <div className="col flex-grow-1">
          <div className="bg-gray p-3 pb-0 rounded-8 mb-3">
            <div className="fs-18 fw-600 color-gray-450 mb-3">
              <FormattedMessage
                defaultMessage="Статус заказа"
                id="purchaseOrderPage.label.orderStatus"
                description="Заголовок на странице"
              />
            </div>
            {order.status === 'retail' ||
            order.transportation?.type === 'combined' ? (
              <RetailDeliveryOrderStatusTable
                order={order}
                events={events ?? []}
              />
            ) : (
              <DeliveryOrderStatusTable order={order} />
            )}
          </div>
          {order.status !== 'retail' &&
            order.transportation?.type !== 'combined' && (
              <div className="bg-gray p-3 rounded-8 my-3">
                <DeliveryOrderPackageGroupInfo id={order.id} />
              </div>
            )}

          <div className="bg-gray p-3 rounded-8">
            <DeliveryOrderDeliveryInfo id={order.id} />
            {!['assembly', 'retail'].includes(order.status) &&
              order.transportation?.type !== 'combined' && (
                <>
                  <hr />
                  <DeliveryOrderAssemblyInfo id={order.id} />
                </>
              )}
          </div>

          <div className="bg-gray p-3 rounded-8 mt-3">
            <ClientInfo user={order.user!} />
          </div>
          <Rbac
            object={RbacObject.DeliveryOrder}
            action={['read:finances-client', 'read:finances-company']}
          >
            <div className="bg-gray p-3 rounded-8 mt-3">
              <DeliveryOrderFinancesInfo id={order.id} />
            </div>
          </Rbac>
          <Rbac object={RbacObject.DeliveryOrder} action={'read:notes'}>
            <NotesInfo type="delivery" id={order.id} />
          </Rbac>
          <Rbac object={RbacObject.DeliveryOrder} action={'read:transactions'}>
            <OrderTransactionsInfo type={'delivery-order'} id={order.id} />
          </Rbac>
          <Rbac
            object={RbacObject.DeliveryOrder}
            action={'read:finances-client'}
          >
            <SupplierInfo supplier={order.supplier?.name} />
          </Rbac>
          <div className="bg-gray p-3 rounded-8 mt-3">
            <RetailDescriptionIfo id={order.id} />
          </div>
        </div>

        <div className="col d-flex flex-column ">
          <div className=" justify-content-center gap-1 pb-2 d-flex flex-row flex-wrap">
            {order.status === 'retail' && (
              <Rbac object={RbacObject.DeliveryOrder} action={'write'}>
                {!order.retailOrder?.description && (
                  <RetailDeliveryDescriptionButton id={order.id} />
                )}
                {!order.retailOrder?.hwbRefNumber && (
                  <RetailDeliveryBulkButton id={order.id} />
                )}
              </Rbac>
            )}
            {order.status !== 'retail' &&
              order.transportationType !== 'combined' && (
                <Rbac object={RbacObject.Cdek} action={'read'}>
                  <DeliveryOrderCdekInfo id={order.id} />
                </Rbac>
              )}
            {order.status === 'receivedInRussia' ||
              (order.status === 'sentToRussia' && (
                <Rbac
                  object={RbacObject.DeliveryOrder}
                  action={'read:warehouse-russia'}
                >
                  <RecipientInfoButton id={order.id} />
                </Rbac>
              ))}
            {!order.retailOrder && order.status === 'receivedInRussia' && (
              <Rbac
                object={RbacObject.DeliveryOrder}
                action={'write:revert-received-in-russia'}
              >
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    await ModalsService.createModal(
                      RevertReceivedInRussiaConfirmationModal,
                      {
                        deliveryOrder: order,
                      },
                    );
                    mutateOrder();
                  }}
                >
                  Вернуть на статус "В пути"
                </button>
              </Rbac>
            )}
            {order.status === 'sentToRecipient' &&
              !order.trackNumber &&
              !order.retailOrder && (
                <button
                  type="button"
                  className={classNames('btn', btnStyle.trackButton)}
                  onClick={getTrackNumberForm}
                >
                  Указать трек-номер
                </button>
              )}
            {nextStepText && (
              <div className={btnClass} onClick={nextStep}>
                {nextStepText} | <i className="bi bi-check-lg fs-18" />
              </div>
            )}
            <Rbac object={RbacObject.DeliveryOrder} action={'delete'}>
              {((order.status === 'assembly' &&
                order.transportationType !== 'combined') ||
                order.status === 'processing') && (
                <div className="btn btn-success" onClick={deleteOrder}>
                  Удалить
                </div>
              )}
            </Rbac>
          </div>

          {order.status === 'sentToRecipient' && !order.retailOrder && (
            <div className="d-flex flex-row justify-content-center mt-3">
              <div
                className={classNames('btn', btnStyle.trackButton)}
                onClick={blockOrder}
              >
                {order.blocked ? (
                  <FormattedMessage
                    defaultMessage="Снять блокировку"
                    id="deliveryOrderScreen.label.Unlock"
                    description="Заголовок"
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="Блокировка выдачи"
                    id="deliveryOrderScreen.label.issueBlocking"
                    description="Заголовок"
                  />
                )}
              </div>
            </div>
          )}
          {!user.accesses.includes('fulfillment') ? (
            <div className="flex-grow-1 d-flex mb-4">
              <div className="flex-grow-1 d-flex mb-4 flex-column">
                <Nav
                  appearance="tabs"
                  activeKey={tab}
                  onSelect={(key) => setSearchParams({ chat: key })}
                >
                  <Nav.Item eventKey="supplier" className="bg-gray-50">
                    <FormattedMessage
                      defaultMessage="Поставщик"
                      id="TransactiosHistrory.lable.supplier"
                      description="Вкладка"
                    />
                  </Nav.Item>
                  {!user.supplier && (
                    <Nav.Item eventKey="client" className="bg-teal-100">
                      <FormattedMessage
                        defaultMessage="Клиент"
                        id="clientInfo.label.customer"
                        description="Заголовок на странице"
                      />
                    </Nav.Item>
                  )}
                </Nav>
                <ConversationWindow
                  id={order.id}
                  type="delivery"
                  internalId={order.internalId}
                  supplier={order.supplier}
                  client={order.user}
                  tab={tab}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
